/* eslint-disable max-lines */
/**
 * Smart function to replace only filter parameters while preserving all other URL parameters (sortBy, sortOrder, etc.)
 * @param {URLSearchParams} urlParams - Current URL search parameters
 * @param {Array} appliedParams - Array of filter strings to apply
 * @returns {string} - New URL with updated filters but preserving other params
 */
export const getReplaceFiltersURL = (urlParams, appliedParams) => {
  // Create a new URLSearchParams object to avoid mutating the original
  const newParams = new URLSearchParams();

  // First, copy all existing parameters except filter-related ones
  for (const [key, value] of urlParams.entries()) {
    if (key !== "filter") {
      newParams.append(key, value);
    }
  }

  // Add new filter parameters
  if (appliedParams?.length > 0) {
    appliedParams.forEach((filter) => {
      newParams.append("filter", encodeURIComponent(filter));
    });
  }

  // Build the final URL
  const newSearch = newParams.toString();
  const url = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
  return url;
};

/**
 * Remove Filters from URL
 * @param {*} urlParams
 * @param {*} param
 * @returns
 */
export const removeFiltersParamsFromURL = (urlParams, param) => {
  urlParams.delete(param || "filter");
  const newSearch = urlParams.toString();
  const url = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
  return url;
};

/**
 *
 * @param {*} urlParams
 * @param {*} appliedParams
 * @param {*} sortOption
 * @returns
 */
export const getReplaceFiltersUrlForMobile = (urlParams, appliedParams, sortOption) => {
  // Create a fresh URLSearchParams to avoid mutating the original
  const newParams = new URLSearchParams();

  // Copy all existing params except filter and sorting ones
  for (const [key, value] of urlParams.entries()) {
    if (key !== "filter" && key !== "sortBy" && key !== "sortOrder") {
      newParams.append(key, value);
    }
  }

  // Add new filter params
  if (appliedParams?.length > 0) {
    appliedParams.forEach((filter) => {
      newParams.append("filter", encodeURIComponent(filter));
    });
  }

  // Add sorting params if available
  if (sortOption?.sortBy) newParams.set("sortBy", sortOption.sortBy);
  if (sortOption?.sortOrder) newParams.set("sortOrder", sortOption.sortOrder);

  // Build the final URL
  const newSearch = newParams.toString();
  return `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
};

/**
 * Convert currency value for display
 * @param {Object} selectedCurrency - Selected currency object
 * @param {number} value - Value to convert
 * @param {Function} updateCurrency - Currency update function from utils/common
 * @returns {number} Converted currency value
 */
export const currencyConverter = (selectedCurrency, value, updateCurrency) => {
  return Math.ceil(
    Number(updateCurrency(selectedCurrency, value, false, false, false)?.split(" ")?.[1]?.replaceAll(",", "") || 0),
  );
};

/**
 * Convert from selected currency back to base currency (INR/API currency)
 * @param {Object} selectedCurrency - Selected currency object
 * @param {number|string} convertedValue - Value in selected currency
 * @param {string} CURRENCY_ID - Base currency ID constant
 * @returns {number} Value in base currency
 */
export const reverseCurrencyConverter = (selectedCurrency, convertedValue, CURRENCY_ID) => {
  if (
    selectedCurrency?.currency?.id &&
    selectedCurrency?.currency?.factor &&
    selectedCurrency?.currency?.id !== CURRENCY_ID
  ) {
    // Convert back to base currency by dividing by factor
    return Math.round(parseFloat(convertedValue) / selectedCurrency.currency.factor);
  }
  // If already in base currency (INR), return as is
  return Math.round(parseFloat(convertedValue));
};

/**
 * Snap value to appropriate step size based on outlier threshold
 * @param {number} value - The value to snap
 * @param {Object} priceOptions - Price options from facet (facetOptions[0])
 * @returns {number} Snapped value
 */
export const snapToStepSize = (value, priceOptions) => {
  if (!priceOptions) return value;
  const roundedMaxPrice = priceOptions.roundedMaxPrice;
  // Ensure value doesn't exceed roundedMaxPrice
  const clampedValue = Math.min(value, roundedMaxPrice);

  if (!priceOptions.isSkewedDistribution) {
    // Not skewed, use regular stepSize
    const stepSize = priceOptions.stepSize;
    return Math.round(clampedValue / stepSize) * stepSize;
  }

  // Find outlier threshold (minPrice of bucket with isOutlier: true)
  const priceBuckets = priceOptions.priceBuckets || [];
  const outlierBucket = priceBuckets.find((bucket) => bucket.isOutlier === true);
  const outlierThreshold = outlierBucket?.minPrice || priceOptions.effectiveMaxPrice || 0;
  const stepSize = priceOptions.stepSize;
  const stepSizeOutlier = priceOptions.stepSizeOutlier;

  if (clampedValue < outlierThreshold) {
    // Before outlier range, use stepSize
    return Math.round(clampedValue / stepSize) * stepSize;
  } else {
    // In outlier range, use stepSizeOutlier
    // Snap to the nearest stepSizeOutlier from the outlier threshold
    const stepsFromThreshold = Math.round((clampedValue - outlierThreshold) / stepSizeOutlier);
    const snappedValue = outlierThreshold + stepsFromThreshold * stepSizeOutlier;
    // Ensure snapped value doesn't exceed roundedMaxPrice
    return Math.min(snappedValue, roundedMaxPrice);
  }
};

/**
 * Parse applied filters from Redux state into selectedOptions and priceRange
 * @param {Array} appliedFilters - Array of filter strings from Redux
 * @returns {Object} Object with selectedOptions and priceRange
 */
export const parseAppliedFilters = (appliedFilters) => {
  const selectedOptionsFromFilters = {};
  let priceRangeFromFilters = null;

  if (!appliedFilters || appliedFilters.length === 0 || (appliedFilters.length === 1 && appliedFilters[0] === "")) {
    return { selectedOptionsFromFilters, priceRangeFromFilters };
  }

  for (const item of appliedFilters) {
    if (!item || typeof item !== "string") continue;

    // split only on the first ":" (value might contain ":")
    const [facetName, ...rest] = item.split(":");
    const filterValue = rest.join(":");

    if (facetName === "defaultPrice" && filterValue.includes("-")) {
      const [minStr, maxStr] = filterValue.split("-");
      const minPrice = parseInt(minStr, 10);
      const maxPrice = parseInt(maxStr, 10);
      if (!Number.isNaN(minPrice) && !Number.isNaN(maxPrice)) {
        priceRangeFromFilters = [minPrice, maxPrice];
      }
    } else if (filterValue) {
      selectedOptionsFromFilters[filterValue] = `${facetName}:${filterValue}`;
    }
  }

  return { selectedOptionsFromFilters, priceRangeFromFilters };
};

/**
 * Build price filter string for API
 * @param {string} facetFieldName - Price facet field name
 * @param {Array} priceRangeFilter - Price range [min, max]
 * @returns {string} Price filter string
 */
export const buildPriceFilterString = (facetFieldName, priceRangeFilter) => {
  if (!priceRangeFilter || priceRangeFilter.length < 2) return null;
  return `${facetFieldName}:${priceRangeFilter[0]}-${priceRangeFilter[1]}`;
};

/**
 * Build filter payload for API request
 * @param {Object} params - Parameters object
 * @param {Array} appliedFilterArray - Array of filter strings
 * @param {Object} sortOption - Sort option object
 * @param {string} selectedPincode - Selected pincode
 * @param {string} intlPincode - International pincode
 * @param {string} catalogId - Catalog ID
 * @param {string} categoryId - Category ID
 * @param {boolean} isSearchPage - Whether this is a search page
 * @param {string} searchQuery - Search query string
 * @param {number} pageSize - Page size constant
 * @param {string} initialPage - Initial page constant
 * @param {string} searchPageType - Search page type constant
 * @param {string} defaultCountry - Default country constant
 * @param {string} email - User email from cookie
 * @param {string} fnpId - User FNP ID from cookie
 * @returns {Object} Payload object for API
 */
export const buildFilterPayload = ({
  appliedFilterArray,
  sortOption,
  selectedPincode,
  intlPincode,
  catalogId,
  categoryId,
  isSearchPage,
  searchQuery,
  pageSize,
  initialPage,
  searchPageType,
  defaultCountry,
  email,
  fnpId,
}) => {
  const payload = {
    page: initialPage,
    size: pageSize,
    geoId: catalogId || defaultCountry,
    categoryUrl: isSearchPage && searchQuery ? null : `fnp.com/${categoryId}`,
    pincode: catalogId !== defaultCountry ? intlPincode : selectedPincode,
    filters: appliedFilterArray,
    sortBy: sortOption?.sortBy,
    sortOrder: sortOption?.sortOrder,
    email: email || "",
    fnpId: fnpId || null,
  };

  // Add search-specific parameters if this is a search page
  if (isSearchPage && searchQuery) {
    payload.pageType = searchPageType;
    payload.qs = encodeURIComponent(searchQuery);
  }

  return payload;
};

/**
 * Get formatted label for filter option
 * @param {Object} option - Filter option object
 * @returns {string} Formatted label
 */
export const getFilterOptionLabel = (option) => {
  return `${option?.displayName} (${option.count})` ?? "";
};

/**
 * Calculate the number of selected options with a count greater than zero
 * Supports both mobile (uses displayName as key) and desktop (uses value as key) filter storage
 * @param {Array} options - Array of filter options
 * @param {Object} selectedOptions - Object of selected options
 * @returns {number} Count of selected options
 */
export const getSelectedOptionsCount = (options, selectedOptions) => {
  return options?.reduce(
    (countZ, option) =>
      countZ +
      (option.count > 0 && (!!selectedOptions?.[option.displayName] || !!selectedOptions?.[option.value]) ? 1 : 0),
    0,
  );
};

/**
 * Compare two numeric ranges and check if they are identical
 * @param {number[]} rangeA - The first range [min, max]
 * @param {number[]} rangeB - The second range [min, max]
 * @returns {boolean} True if both ranges are the same, otherwise false
 */
export const isSameRange = (rangeA, rangeB) =>
  Array.isArray(rangeA) &&
  Array.isArray(rangeB) &&
  rangeA.length === rangeB.length &&
  rangeA[0] === rangeB[0] &&
  rangeA[1] === rangeB[1];

/**
 * Check if two objects have the same keys and values
 * @param {Object} obj1 - The first object to compare
 * @param {Object} obj2 - The second object to compare
 * @returns {boolean} True if both objects have the same keys and values, otherwise false
 */
export const areObjectsEqual = (obj1, obj2) => {
  if (!obj1 && !obj2) return true;
  if (!obj1 || !obj2) return false;
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;
  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) return false;
  }
  return true;
};

/**
 * Sanitize numeric input value - removes non-numeric characters and leading zeros
 * @param {string} value - Input value to sanitize
 * @returns {string} Sanitized numeric value
 */
export const sanitizeNumericInput = (value) => {
  if (value === "") {
    return "";
  }

  // Remove non-numeric characters and leading zeros
  let numericValue = value.replace(/[^0-9]/g, "");

  // Remove leading zeros but keep single zero
  if (numericValue.length > 1) {
    numericValue = numericValue.replace(/^0+/, "");
  }

  // If all zeros were removed, keep it as empty string
  if (numericValue === "") {
    return "";
  }

  return numericValue;
};

/**
 * Calculate and validate price range from user input
 * @param {string} minPrice - Minimum price input (in selected currency)
 * @param {string} maxPrice - Maximum price input (in selected currency)
 * @param {Object} priceOption - Price option from facet (facetOptions[0])
 * @param {Function} reverseCurrencyConverter - Function to convert from selected currency to base currency
 * @returns {Object} Object with finalMinBase, finalMaxBase, and isValid flag
 */
export const calculatePriceRangeFromInput = (minPrice, maxPrice, priceOption, reverseCurrencyConverter) => {
  const minBound = priceOption?.roundedMinPrice || 0;
  const maxBound = priceOption?.roundedMaxPrice || 100000;

  // User input (minPrice/maxPrice) is in selected currency, convert to base currency (INR)
  const minValueInBase = minPrice === "" ? minBound : reverseCurrencyConverter(minPrice);
  const maxValueInBase = maxPrice === "" ? maxBound : reverseCurrencyConverter(maxPrice);

  // Clamp to valid bounds (in base currency)
  const clampedMin = Math.max(minBound, Math.min(minValueInBase, maxBound));
  const clampedMax = Math.max(minBound, Math.min(maxValueInBase, maxBound));

  // Ensure min is not greater than max (in base currency)
  const finalMinBase = Math.min(clampedMin, clampedMax);
  const finalMaxBase = Math.max(clampedMin, clampedMax);

  return {
    finalMinBase,
    finalMaxBase,
    isValid: true,
  };
};

/**
 * Get filter tab count for a specific facet
 * @param {Object} facet - Facet object
 * @param {Object} selectedOptions - Selected options object
 * @returns {number} Count of selected options for this facet
 */
export const getFilterTabCount = (facet, selectedOptions) => {
  return Object.values(selectedOptions)?.reduce(
    (acc, value) => acc + (facet?.facetFieldName === value.split(":")[0] ? 1 : 0),
    0,
  );
};

/**
 * Calculate total count for a facet
 * @param {Array} facetOptions - Array of facet options
 * @returns {number} Total count
 */
export const getFacetTotalCount = (facetOptions) => {
  return facetOptions?.reduce((acc, option) => acc + (option?.count || 0), 0);
};

/**
 * Convert actual price value to slider position (0-100)
 * For skewed distribution: 70% of slider (0-70) maps to prices from roundedMinPrice to effectiveMaxPrice
 *                          30% of slider (70-100) maps to prices from effectiveMaxPrice to roundedMaxPrice
 * For normal distribution: Linear mapping from roundedMinPrice to roundedMaxPrice
 * @param {number} price - The actual price value
 * @param {Object} priceOptions - Price options from facet (facetOptions[0])
 * @returns {number} Slider position (0-100)
 */
export const priceToSliderValue = (price, priceOptions) => {
  if (!priceOptions) return 0;

  const roundedMinPrice = priceOptions.roundedMinPrice || 0;
  const roundedMaxPrice = priceOptions.roundedMaxPrice || 100000;
  const effectiveMaxPrice = priceOptions.effectiveMaxPrice;

  // Clamp price to valid bounds
  const clampedPrice = Math.max(roundedMinPrice, Math.min(price, roundedMaxPrice));

  if (priceOptions.isSkewedDistribution && effectiveMaxPrice) {
    // Skewed distribution: 70-30 split
    if (clampedPrice <= effectiveMaxPrice) {
      // First 70% of slider maps to prices from roundedMinPrice to effectiveMaxPrice
      const priceRange = effectiveMaxPrice - roundedMinPrice;
      if (priceRange <= 0) return 0;
      const ratio = (clampedPrice - roundedMinPrice) / priceRange;
      return ratio * 70;
    } else {
      // Last 30% of slider maps to prices from effectiveMaxPrice to roundedMaxPrice
      const priceRange = roundedMaxPrice - effectiveMaxPrice;
      if (priceRange <= 0) return 100;
      const ratio = (clampedPrice - effectiveMaxPrice) / priceRange;
      return 70 + ratio * 30;
    }
  } else {
    // Normal distribution: Linear mapping
    const priceRange = roundedMaxPrice - roundedMinPrice;
    if (priceRange <= 0) return 0;
    const ratio = (clampedPrice - roundedMinPrice) / priceRange;
    return ratio * 100;
  }
};

/**
 * Convert slider position (0-100) to actual price value
 * For skewed distribution: 70% of slider (0-70) maps to prices from roundedMinPrice to effectiveMaxPrice
 *                          30% of slider (70-100) maps to prices from effectiveMaxPrice to roundedMaxPrice
 * For normal distribution: Linear mapping from roundedMinPrice to roundedMaxPrice
 * @param {number} sliderValue - The slider position (0-100)
 * @param {Object} priceOptions - Price options from facet (facetOptions[0])
 * @returns {number} Actual price value
 */
export const sliderValueToPrice = (sliderValue, priceOptions) => {
  if (!priceOptions) return 0;

  const roundedMinPrice = priceOptions.roundedMinPrice || 0;
  const roundedMaxPrice = priceOptions.roundedMaxPrice || 100000;
  const effectiveMaxPrice = priceOptions.effectiveMaxPrice;

  // Clamp slider value to valid bounds
  const clampedSliderValue = Math.max(0, Math.min(sliderValue, 100));

  if (priceOptions.isSkewedDistribution && effectiveMaxPrice) {
    // Skewed distribution: 70-30 split
    if (clampedSliderValue <= 70) {
      // First 70% of slider maps to prices from roundedMinPrice to effectiveMaxPrice
      const ratio = clampedSliderValue / 70;
      const priceRange = effectiveMaxPrice - roundedMinPrice;
      return roundedMinPrice + ratio * priceRange;
    } else {
      // Last 30% of slider maps to prices from effectiveMaxPrice to roundedMaxPrice
      const ratio = (clampedSliderValue - 70) / 30;
      const priceRange = roundedMaxPrice - effectiveMaxPrice;
      return effectiveMaxPrice + ratio * priceRange;
    }
  } else {
    // Normal distribution: Linear mapping
    const ratio = clampedSliderValue / 100;
    const priceRange = roundedMaxPrice - roundedMinPrice;
    return roundedMinPrice + ratio * priceRange;
  }
};

/**
 * Build filter string from selected options and price range
 * @param {Object} selectedOptions - Object of selected filter options
 * @param {Array} priceRange - Price range array [min, max] in INR
 * @returns {string} Comma-separated filter string
 */
export const buildFilterString = (selectedOptions, priceRange) => {
  try {
    const filterArray = [];

    // Add all selected options (format: "facetName : filterValue")
    if (selectedOptions && typeof selectedOptions === "object") {
      try {
        Object.values(selectedOptions).forEach((filterString) => {
          if (filterString && typeof filterString === "string") {
            // Convert "facetName:filterValue" to "facetName : filterValue" format
            const formattedFilter = filterString.replace(":", " : ");
            if (formattedFilter) {
              filterArray.push(formattedFilter);
            }
          }
        });
      } catch (error) {
        console.error("Error processing selected options:", error);
      }
    }

    // Add price filter if selected
    if (priceRange && Array.isArray(priceRange) && priceRange.length === 2) {
      try {
        const [minPrice, maxPrice] = priceRange;
        // Validate that both prices are valid numbers
        if (
          minPrice != null &&
          maxPrice != null &&
          !Number.isNaN(Number(minPrice)) &&
          !Number.isNaN(Number(maxPrice))
        ) {
          // Send prices directly in Indian Rupee (INR) without conversion
          filterArray.push(`defaultPrice : ${minPrice}-${maxPrice}`);
        }
      } catch (error) {
        console.error("Error processing price range:", error);
      }
    }

    return filterArray.join(",");
  } catch (error) {
    console.error("Error building filter string:", error);
    return "";
  }
};

/**
 * Track Filter Selected event for price filter
 * @param {Array} priceRange - Price range array [min, max] in INR
 * @param {Function} trackEvent - Analytics trackEvent function
 * @param {Object} plpCleverTapEvents - CleverTap events constants
 * @param {string} pathname - Current page pathname
 * @param {string} pageType - Page type constant
 */
export const trackPriceFilterSelected = (priceRange, trackEvent, plpCleverTapEvents, pathname, pageType) => {
  try {
    if (
      priceRange &&
      Array.isArray(priceRange) &&
      priceRange.length === 2 &&
      trackEvent &&
      plpCleverTapEvents?.FILTER_SELECTED
    ) {
      const [minPrice, maxPrice] = priceRange;
      // Validate that both prices are valid numbers
      if (minPrice != null && maxPrice != null && !Number.isNaN(Number(minPrice)) && !Number.isNaN(Number(maxPrice))) {
        // Send prices directly in Indian Rupee (INR) without conversion
        const filterValue = `${minPrice}-${maxPrice}`;

        trackEvent(
          plpCleverTapEvents.FILTER_SELECTED,
          {
            page_name: pathname || "",
            page_type: pageType || "",
            filter: `defaultPrice : ${filterValue}`,
          },
          ["clevertap"],
        );
      }
    }
  } catch (error) {
    console.error("Error tracking price filter selected:", error);
  }
};

/**
 * Track Filter Applied event when apply filters button is clicked
 * @param {Object} selectedOptions - Object of selected filter options
 * @param {Array} priceRange - Price range array [min, max] in INR
 * @param {Function} trackEvent - Analytics trackEvent function
 * @param {Object} plpCleverTapEvents - CleverTap events constants
 * @param {string} pathname - Current page pathname
 * @param {string} pageType - Page type constant
 */
export const trackFilterApplied = (selectedOptions, priceRange, trackEvent, plpCleverTapEvents, pathname, pageType) => {
  try {
    if (!trackEvent || !plpCleverTapEvents?.FILTER_APPLIED) {
      return;
    }

    const filterString = buildFilterString(selectedOptions, priceRange);

    if (filterString) {
      trackEvent(
        plpCleverTapEvents.FILTER_APPLIED,
        {
          page_name: pathname || "",
          page_type: pageType || "",
          filter: filterString,
        },
        ["clevertap"],
      );
    }
  } catch (error) {
    console.error("Error tracking filter applied event:", error);
  }
};
